<template>
    <div class="space-y-4 mt-6">
        <div :class="[
            showEmotions ? 'justify-between' : 'justify-end',
            'flex items-center',
            { 'border-b-[1.5px] border-current  pb-2': !noBorder },
        ]">
            <Emotions v-if="showEmotions" :initialEmotions="modelEmotions" :availableEmotions="availableEmotions"
                :modelId="modelId" :modelClass="modelClass" :rootModelClass="modelClass" :rootModelId="modelId" />
            <Transition name="fade" mode="out-in">
                <p v-if="commentsEnabled" class="text-sm" :key="commentCount">
                    {{ commentCount + ' ' + $lang.get('Comments') }}
                </p>
            </Transition>
            <div v-if="limitedComments" class="flex flex-col gap-x-2 items-end">
                <TextTooltip :text="$lang.get('Go to conversation')" roundedClass="rounded-full">
                    <Link :href="$localizedRoute(modelClass + '.show', modelId)" :data="{
                        comments: true,
                    }">
                    <BaseButton size="sm"
                        colors="bg-resource border border-resource-accent hover:bg-resource-accent hover:text-resource"
                        class="flex gap-x-2 items-center">
                        <p v-if="commentCount > 0" v-text="$lang.get(':count comments', {
                            count: commentCount,
                        })
                            " />
                        <PhChatCircleText class="size-6" weight="regular" />
                    </BaseButton>
                    </Link>
                </TextTooltip>
            </div>
        </div>
        <Comments v-if="showComments" :modelId="modelId" :modelClass="modelClass"
            :availableEmotions="availableEmotions" />
    </div>
</template>

<script>
import { PhPaperPlaneRight, PhChatCircleText } from '@phosphor-icons/vue'

import Emotions from '@/Components/ModelComponents/Interactions/Emotions.vue'
import Comments from '@/Components/ModelComponents/Interactions/Comments.vue'
import TextAreaInput from '@/Components/FormElements/TextAreaInput.vue'
import BaseButton from '@/Components/Elements/BaseButton.vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'
import { Link } from '@inertiajs/vue3'

export default {
    name: 'ModelInteractions',

    components: {
        PhPaperPlaneRight,
        TextAreaInput,
        Emotions,
        Comments,
        BaseButton,
        PhChatCircleText,
        TextTooltip,
        Link,
    },

    props: {
        modelId: {
            type: Number,
            required: true,
        },
        modelClass: {
            type: String,
            required: true,
        },
        availableEmotions: {
            type: Array,
            default: () => [],
        },
        modelEmotions: {
            type: Array,
            default: () => [],
        },
        initialCommentCount: {
            type: Number,
            default: 0,
        },
        interactivityId: {
            type: String,
            default: null,
        },
        limitedComments: {
            type: Boolean,
            default: false,
        },
        noBorder: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            echoChannel: null,
            commentCount: this.initialCommentCount,
        }
    },

    mounted() {
        this.initEcho()
    },

    beforeUnmount() {
        this.destroyEcho()
    },

    computed: {
        commentsEnabled() {
            return (
                [null, 'fullinteractivity', 'createinteractivity'].includes(
                    this.interactivityId
                ) && !this.limitedComments
            )
        },

        /**
         * A boolean that determines if the comments should be shown, depends on the models interactivity_id
         */
        showComments() {
            return this.commentsEnabled && this.$page.props?.auth?.user
        },

        /**
         * A boolean that determines if the interactions should be shown, depends on the models interactivity_id
         */
        showEmotions() {
            return (
                [
                    null,
                    '',
                    'fullinteractivity',
                    'createinteractivity',
                    'likeinteractivity',
                ].includes(this.interactivityId) &&
                this.availableEmotions.length > 0
            )
        },
    },

    methods: {
        initEcho() {
            if (this.$page.props?.auth?.user) {
                this.echoChannel = window.Echo.private(
                    `${this.modelClass}-${this.modelId}-interactions`
                )

                this.echoChannel.listen(
                    'CommentCountUpdatedEvent',
                    (event) => (this.commentCount = event.commentCount)
                )
            }
        },

        destroyEcho() {
            if (this.$page.props?.auth?.user) {
                window.Echo.leaveChannel(
                    `${this.rootModelClass}-${this.rootModelId}-interactions`
                )
                this.echoChannel = null
            }
        },
    },
}
</script>
